/**
 * Chip — single primitive for tonal pills.
 *
 * Tones map to a quiet, premium palette. We avoid loud colors except for
 * "red" which signals urgency. Default tone is "stone" (neutral).
 *
 * Usage:
 *   <Chip tone="amber" size="xs">Acción vencida</Chip>
 *   <Chip dot tone="emerald">Reservado</Chip>
 */
import React from "react";

const TONES = {
  stone:   { bg: "bg-stone-100",    text: "text-stone-700",    border: "border-stone-200",    dot: "bg-stone-400"   },
  slate:   { bg: "bg-slate-100",    text: "text-slate-700",    border: "border-slate-200",    dot: "bg-slate-400"   },
  blue:    { bg: "bg-blue-50",      text: "text-blue-800",     border: "border-blue-200",     dot: "bg-blue-500"    },
  cyan:    { bg: "bg-cyan-50",      text: "text-cyan-800",     border: "border-cyan-200",     dot: "bg-cyan-500"    },
  violet:  { bg: "bg-violet-50",    text: "text-violet-800",   border: "border-violet-200",   dot: "bg-violet-500"  },
  amber:   { bg: "bg-amber-50",     text: "text-amber-900",    border: "border-amber-300",    dot: "bg-amber-500"   },
  emerald: { bg: "bg-emerald-50",   text: "text-emerald-800",  border: "border-emerald-200",  dot: "bg-emerald-500" },
  red:     { bg: "bg-red-50",       text: "text-red-800",      border: "border-red-200",      dot: "bg-red-500"     },
};

const SIZES = {
  xs: "text-[10px] px-1.5 py-0.5 gap-1",
  sm: "text-[11px] px-2   py-0.5 gap-1",
  md: "text-xs    px-2.5 py-1   gap-1.5",
};

export default function Chip({
  tone = "stone",
  size = "sm",
  dot = false,
  dotColor,
  icon,
  children,
  className = "",
  title,
  ...rest
}) {
  const t = TONES[tone] || TONES.stone;
  const s = SIZES[size] || SIZES.sm;
  return (
    <span
      title={title}
      className={`inline-flex items-center rounded-full border ${t.bg} ${t.text} ${t.border} ${s} ${className}`}
      {...rest}
    >
      {dot && (
        <span
          className={`shrink-0 w-1.5 h-1.5 rounded-full`}
          style={dotColor ? { background: dotColor } : undefined}
          // fallback to tone dot if no explicit color
          {...(!dotColor ? { className: `shrink-0 w-1.5 h-1.5 rounded-full ${t.dot}` } : {})}
        />
      )}
      {icon}
      <span className="truncate">{children}</span>
    </span>
  );
}
